import './Button.css'

import { useState } from 'react'

import type { FieldSize } from './field-size'

import { useFocus } from '../hooks/useFocus'
import { cx } from '../utilities/cx'
import { Icon, IconName } from './Icon'

export type ButtonKind = 'primary' | 'secondary' | 'ghost' | 'destructive'

export type ButtonProps = {
  className?: string
  kind?: ButtonKind
  size?: FieldSize
  disabled?: boolean
  focusable?: boolean
  focusOrdering?: string
  type?: 'button' | 'submit'
  icon?: IconName
  leftIcon?: IconName
  rightIcon?: IconName
  text?: string
  ariaLabel?: string
  onClick?: () => void
  children?: React.ReactNode
}

export const Button = (props: ButtonProps) => {
  const {
    className,
    kind = 'primary',
    size,
    disabled,
    focusable = true,
    focusOrdering,
    type = 'button',
    icon,
    leftIcon = icon,
    rightIcon,
    text,
    ariaLabel,
    onClick,
    children,
  } = props

  const focus = useFocus()
  const [isActive, setIsActive] = useState(false)

  const hasHandler = type === 'submit' || !!onClick
  const isDisabled = !hasHandler || !!disabled

  const handleMouseDown = () => {
    if (isDisabled) return
    setIsActive(true)
  }

  const handleMouseUp = () => {
    if (isDisabled) return
    setIsActive(false)
    onClick?.()
  }

  const handleMouseLeave = () => {
    setIsActive(false)
  }

  const classNames = cx(
    'button',
    `button--${kind}`,
    {
      'button--small': size === 'small',
      'button--active': isActive,
      'button--disabled': isDisabled,
    },
    className,
  )

  return (
    <button
      className={classNames}
      type={type}
      disabled={isDisabled}
      aria-label={ariaLabel}
      ref={focus.ref({ focusable, disabled: isDisabled, ordering: focusOrdering })}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {leftIcon && <Icon className="button__icon" name={leftIcon} />}
      {text && <span className="button__text">{text}</span>}
      {children}
      {rightIcon && <Icon className="button__icon" name={rightIcon} />}
    </button>
  )
}
